import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Elements, useStripe } from '@stripe/react-stripe-js'
import { Stripe, StripeElementsOptions } from '@stripe/stripe-js'

import { TContent, TSelectLTPPage } from '@/config/types'
import { PageName } from '@/enums/pageName'
import { formatCurrency } from '@/utils/currencyUtils'
import { getContentDefaultStripeOptions } from '@/utils/stripeUtils'
import { getPath } from '@/utils/urlUtils'

import { PrimaryButtonWithIcon, TextButton } from '@/components/buttons'
import { TextBlock } from '@/components/textBlock'

import { ApiService } from '@/api'
import { TFastPurchaseContentPayload } from '@/api/types'
import { useAuth } from '@/auth/useAuth'

import { TSelectContentBlockControl, SelectLTPResult, TNextPageFn } from './types'

export type TFastPaymentBlockProps = {
  pageConfig: TSelectLTPPage
  contents: TContent[]
  selectedContent: TContent
  selectContent: (content: TContent) => void
  scrollToAnchor: () => void
  onClick: TNextPageFn
  className?: string
  buttonName: string
  buttonClassName?: string
  skipButtonName?: string
  containerClassName?: string
  stripeNextActionConfig: {
    stripePromise?: Promise<Stripe | null>
    backendSuccessRedirectTo: PageName
  }
}

const FastPaymentButton = ({
  selectedContent,
  buttonName,
  buttonClassName,
  onClick,
  stripeNextActionConfig,
}: TFastPaymentBlockProps) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const stripe = useStripe()

  const processClick = useCallback(async () => {
    if (!user) {
      return onClick(SelectLTPResult.CONTINUE)
    }

    const payload: TFastPurchaseContentPayload = {
      page_set_id: window.config.PAGE_SET_ID,
      internal_content_price_id: selectedContent.internalPriceId,
      success_page: stripeNextActionConfig.backendSuccessRedirectTo,
      fail_page: PageName.ltpPaymentFailed,
    }
    setIsLoading(true)
    try {
      const response = await ApiService.fastPurchaseContent(payload)
      setIsLoading(false)
      if (!response.data.client_secret) {
        onClick(SelectLTPResult.FAST_SUCCESS)
        return
      }

      if (!stripe) {
        // stripe isn't loaded yet - nothing we can do
        navigate(getPath(PageName.ltpPaymentFailed))
        return
      }

      const { error } = await stripe.handleNextAction({
        clientSecret: response.data.client_secret,
      })

      if (error) {
        navigate(getPath(PageName.ltpPaymentFailed))
      } else {
        onClick(SelectLTPResult.FAST_SUCCESS)
        return
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setIsLoading(false)
      if (error.response?.status === 409) {
        onClick(SelectLTPResult.FAST_SUCCESS)
        return
      }

      console.log('fast payment error', error)
      onClick(SelectLTPResult.CONTINUE)
      return
    }
  }, [
    user,
    selectedContent,
    navigate,
    onClick,
    stripe,
    stripeNextActionConfig.backendSuccessRedirectTo,
  ])

  let submitButtonClassName = 'select-btn '
  if (buttonClassName) {
    submitButtonClassName += buttonClassName
  }

  return (
    <div className="button-container prime">
      <PrimaryButtonWithIcon
        onClick={processClick}
        isLoading={isLoading}
        className={submitButtonClassName}
        text={buttonName}
      />
    </div>
  )
}

export const FastPaymentBlock = forwardRef<TSelectContentBlockControl, TFastPaymentBlockProps>(
  function FastPaymentBlock(
    {
      pageConfig,
      selectedContent,
      skipButtonName,
      containerClassName,
      onClick,
      stripeNextActionConfig,
      ...props
    }: TFastPaymentBlockProps,
    ref
  ) {
    const { paymentDisclaimer } = pageConfig

    const inputRef = useRef<HTMLDivElement>(null)

    const disclaimer = useMemo(() => {
      return paymentDisclaimer.replace(
        '{price}',
        formatCurrency(selectedContent.price, selectedContent.currency, 2)
      )
    }, [paymentDisclaimer, selectedContent])

    useImperativeHandle(
      ref,
      () => {
        return {
          scrollIntoView() {
            inputRef.current?.scrollIntoView()
          },
        }
      },
      []
    )

    let _containerClassName = 'select-plan-block-wrapper '
    if (containerClassName) {
      _containerClassName += containerClassName
    }

    const skipClick = useCallback(async () => {
      return onClick(SelectLTPResult.SKIP)
    }, [onClick])

    const stripeOptions = useMemo<StripeElementsOptions | undefined>(
      () => getContentDefaultStripeOptions(selectedContent),
      [selectedContent]
    )

    return (
      <Elements stripe={stripeNextActionConfig.stripePromise || null} options={stripeOptions}>
        <div className={_containerClassName} ref={inputRef}>
          <FastPaymentButton
            pageConfig={pageConfig}
            selectedContent={selectedContent}
            skipButtonName={skipButtonName}
            containerClassName={containerClassName}
            onClick={onClick}
            stripeNextActionConfig={stripeNextActionConfig}
            {...props}
          />
          {skipButtonName ? (
            <div className="button-container skip">
              <TextButton onClick={skipClick}>{skipButtonName}</TextButton>
            </div>
          ) : null}

          <TextBlock className="payment-disclaimer" text={disclaimer}></TextBlock>
        </div>
      </Elements>
    )
  }
)
